// Enhanced SEO utilities for better search engine optimization

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  locale?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

export const generateStructuredData = (data: SEOData & { 
  structuredDataType: 'Organization' | 'LocalBusiness' | 'Product' | 'Article' | 'RealEstateAgent' 
}) => {
  const baseUrl = 'https://maskan-student-nest.lovable.app';
  
  switch (data.structuredDataType) {
    case 'Organization':
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Maskan",
        "alternateName": "مسكن",
        "url": baseUrl,
        "logo": `${baseUrl}/favicon.png`,
        "description": data.description,
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "EG",
          "addressRegion": "Cairo"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Customer Service",
          "availableLanguage": ["English", "Arabic"]
        }
      };

    case 'LocalBusiness':
      return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Maskan",
        "description": data.description,
        "url": data.url || baseUrl,
        "telephone": "+20-XXX-XXXX",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "EG",
          "addressRegion": "Cairo"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 30.0444,
          "longitude": 31.2357
        },
        "openingHours": "Mo-Su 00:00-23:59",
        "priceRange": "$$",
        "servedCuisine": "Student Housing Services"
      };

    case 'RealEstateAgent':
      return {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        "name": "Maskan",
        "description": data.description,
        "url": data.url || baseUrl,
        "serviceArea": {
          "@type": "Country",
          "name": "Egypt"
        },
        "areaServed": "Egypt",
        "knowsAbout": ["Student Housing", "University Accommodation", "Rental Properties"]
      };

    case 'Product':
      return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": data.title,
        "description": data.description,
        "image": data.image,
        "url": data.url,
        "brand": {
          "@type": "Brand",
          "name": "Maskan"
        },
        "category": "Student Housing",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "EGP"
        }
      };

    case 'Article':
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": data.title,
        "description": data.description,
        "image": data.image,
        "author": {
          "@type": "Organization",
          "name": "Maskan"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Maskan",
          "logo": {
            "@type": "ImageObject",
            "url": `${baseUrl}/favicon.png`
          }
        },
        "datePublished": data.publishedTime,
        "dateModified": data.modifiedTime,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": data.url
        }
      };

    default:
      return null;
  }
};

export const generateBreadcrumbStructuredData = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

export const generateFAQStructuredData = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateSearchActionStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://maskan-student-nest.lovable.app",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://maskan-student-nest.lovable.app/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };
};

// SEO keywords for different pages
export const seoKeywords = {
  homepage: "student housing Egypt, university accommodation Cairo, verified student apartments, Egyptian university housing, student rental Egypt, safe student housing",
  search: "search student housing Egypt, find university accommodation, student apartments near universities, Cairo student housing search",
  listings: "student housing listings Egypt, university accommodation listings, verified student apartments, student rental properties Egypt",
  listProperty: "list student property Egypt, rent to students, student housing landlord, university accommodation listing",
  about: "about Maskan, student housing platform Egypt, trusted accommodation service, verified housing Egypt",
  contact: "contact Maskan, student housing support Egypt, accommodation help, housing assistance Egypt"
};

// Generate canonical URLs
export const generateCanonicalUrl = (path: string, lang?: string): string => {
  const baseUrl = 'https://maskan-student-nest.lovable.app';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return lang && lang !== 'en' ? `${baseUrl}${cleanPath}?lang=${lang}` : `${baseUrl}${cleanPath}`;
};

// Generate hreflang links
export const generateHreflangLinks = (path: string): Array<{ hreflang: string; href: string }> => {
  const baseUrl = 'https://maskan-student-nest.lovable.app';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  return [
    { hreflang: 'en', href: `${baseUrl}${cleanPath}` },
    { hreflang: 'ar', href: `${baseUrl}${cleanPath}?lang=ar` },
    { hreflang: 'x-default', href: `${baseUrl}${cleanPath}` }
  ];
};